import { useMemo, CSSProperties } from "react";
import { Box } from "rebass/styled-components";
import styled from "styled-components";

import { useDisplayAd } from "MVVM/Hooks/useDisplayAd";
import { useAds } from "MVVM/Providers";
import { useUser } from "MVVM/Hooks/useUser";
import { SlotSize } from "MVVM/Utilities/Ads/DisplayAdManager";

export type AdUnitType =
  | "banner"
  | "large-leaderboard"
  | "large-rectangle"
  | "large-skyscraper"
  | "medium-rectangle"
  | "mobile-leaderboard"
  | "skyscraper"
  | "wide-skyscraper"
  | "small-square";

const AD_SIZES: Record<AdUnitType, SlotSize[]> = {
  banner: [[468, 60]],
  "large-leaderboard": [[728, 90]],
  "large-rectangle": [[336, 280]],
  "large-skyscraper": [[300, 600]],
  "medium-rectangle": [[300, 250]],
  "mobile-leaderboard": [
    [320, 50],
    [320, 100],
  ],
  skyscraper: [[120, 600]],
  "wide-skyscraper": [[300, 600]],
  "small-square": [[200, 200]],
};

interface AdUnitProps {
  slotId: string;
  adUnitType: AdUnitType;
  game?: string;
  onAdLoaded?: (hasAd: boolean) => void;
  collapseIfEmpty?: boolean;
  styles?: CSSProperties;
}

const AdUnit = ({
  slotId,
  adUnitType,
  game,
  onAdLoaded,
  collapseIfEmpty = false,
  styles,
}: AdUnitProps) => {
  const { config } = useAds();
  const { allstarUser } = useUser();

  const userGroupType = allstarUser?.user?.userGroup?.type;
  const isAdFreeUser =
    userGroupType === "PRO" || userGroupType === "CREATOR_PLAT";

  const adSizes = AD_SIZES[adUnitType];
  const primarySize = adSizes[0];

  if (isAdFreeUser) {
    return null;
  }

  const displayConfig = useMemo(() => {
    if (!config?.display?.enabled) return null;
    return {
      networkId: config.display.networkId,
      adUnit: adUnitType,
    };
  }, [config?.display, adUnitType]);

  const { containerRef, hasAd, isResolved } = useDisplayAd({
    slotId,
    sizes: adSizes,
    displayConfig,
    game,
    enabled: true,
    onAdLoaded,
  });

  const shouldCollapse = collapseIfEmpty && isResolved && !hasAd;

  return (
    <AdWrapper $collapsed={shouldCollapse} style={styles}>
      <AdContainer
        ref={containerRef}
        id={slotId}
        $width={primarySize[0]}
        $height={primarySize[1]}
        $hasAd={hasAd}
        $collapsed={shouldCollapse}
      />
    </AdWrapper>
  );
};

const AdWrapper = styled(Box)<{ $collapsed: boolean }>`
  position: relative;
  display: ${({ $collapsed }) => ($collapsed ? "none" : "block")};
`;

const AdContainer = styled(Box)<{
  $width: number;
  $height: number;
  $hasAd: boolean;
  $collapsed: boolean;
}>`
  width: ${({ $width, $collapsed }) => ($collapsed ? 0 : $width)}px;
  height: ${({ $height, $collapsed }) => ($collapsed ? 0 : $height)}px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
`;

export { AdUnit };
